import { History, Bike, Clock, DollarSign, CheckCircle, Calendar, AlertCircle, X } from "lucide-react";
import { useState, useEffect, useMemo } from "react";
import { useAuth } from "../contexts/AuthContext";
import { apiUrl, assetUrl } from "../api";
import { overlapsRange, todayInput, daysAgoInput } from "../dateRange";

interface ParkingSession {
    id: number;
    vehicle_id: number;
    entry_time: string;
    exit_time?: string;
    fee: number;
    status: string;
    payment_status: string;
    entry_image_url?: string | null;
    exit_image_url?: string | null;
    exit_source?: "ONLINE" | "OFFLINE_SYNC" | "MANUAL" | null;
    vehicle: {
        license_plate: string;
        brand?: string;
        model?: string;
    };
}

interface HistoryStats {
    totalSessions: number;
    completedSessions: number;
    currentParking: number;
    totalAmount: number;
}

export function HistoryPage() {
    const { user } = useAuth();
    const [historyData, setHistoryData] = useState<ParkingSession[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string>("");
    const [showDateFilter, setShowDateFilter] = useState(false);
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");

    const filteredData = useMemo(
        () => historyData.filter((s) => overlapsRange(s.entry_time, s.exit_time, fromDate, toDate)),
        [historyData, fromDate, toDate]
    );
    const isFiltered = !!(fromDate || toDate);

    // Tổng chi phí chỉ tính các lượt đã ra (phiên đang gửi chưa phát sinh phí).
    const stats: HistoryStats = useMemo(() => {
        const completed = filteredData.filter((s) => s.exit_time);
        return {
            totalSessions: filteredData.length,
            completedSessions: completed.length,
            currentParking: filteredData.length - completed.length,
            totalAmount: completed.reduce((sum, s) => sum + s.fee, 0),
        };
    }, [filteredData]);

    useEffect(() => {
        if (user?.id) {
            fetchParkingHistory();
        }
    }, [user?.id]);

    const fetchParkingHistory = async () => {
        if (!user?.id) return;

        try {
            setIsLoading(true);
            setError("");

            // Fetch parking sessions for user
            const response = await fetch(apiUrl(`/users/${user.id}/parking-sessions`));
            if (!response.ok) {
                throw new Error('Không thể tải dữ liệu');
            }

            const sessions = await response.json();
            setHistoryData(sessions);

        } catch (error) {
            console.error('Error fetching parking history:', error);
            setError('Không thể tải lịch sử gửi xe');
        } finally {
            setIsLoading(false);
        }
    };

    const quickStats = [
        {
            title: "Tổng lượt gửi",
            value: isLoading ? "..." : stats.totalSessions.toString(),
            icon: Bike,
            color: "bg-gradient-to-r from-cyan-500 to-cyan-600"
        },
        {
            title: "Đã hoàn thành",
            value: isLoading ? "..." : stats.completedSessions.toString(),
            icon: CheckCircle,
            color: "bg-gradient-to-r from-emerald-500 to-emerald-600"
        },
        {
            title: "Đang gửi",
            value: isLoading ? "..." : stats.currentParking.toString(),
            icon: Clock,
            color: "bg-gradient-to-r from-amber-500 to-amber-600"
        },
        {
            title: isFiltered ? "Chi phí (đang lọc)" : "Tổng chi phí",
            value: isLoading ? "..." : `${stats.totalAmount.toLocaleString('vi-VN')}₫`,
            icon: DollarSign,
            color: "bg-gradient-to-r from-violet-500 to-violet-600"
        }
    ];

    const isUnpaid = (session: ParkingSession) => !!session.exit_time && session.payment_status !== "Da_thanh_toan";

    const getStatusIcon = (session: ParkingSession) => {
        if (isUnpaid(session)) return <AlertCircle className="h-4 w-4 text-red-600" />;
        if (session.exit_time) return <CheckCircle className="h-4 w-4 text-emerald-600" />;
        return <Clock className="h-4 w-4 text-amber-600" />;
    };

    const getStatusColor = (session: ParkingSession) => {
        if (isUnpaid(session)) return "bg-red-100 text-red-800";
        if (session.exit_time) return "bg-emerald-100 text-emerald-800";
        return "bg-amber-100 text-amber-800";
    };

    const getStatusText = (session: ParkingSession) => {
        if (isUnpaid(session)) return "Chưa thanh toán";
        return session.exit_time ? "Hoàn thành" : "Đang gửi";
    };

    const formatDateTime = (dateString: string) => {
        return new Date(dateString).toLocaleString('vi-VN', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (isLoading) {
        return (
            <div className="space-y-8">
                <div className="bg-gradient-to-r from-cyan-600 via-blue-600 to-cyan-800 rounded-2xl p-4 lg:p-8 text-white shadow-2xl">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
                        <div>
                            <h1 className="text-2xl lg:text-3xl font-bold mb-2">Lịch sử gửi xe</h1>
                            <p className="text-cyan-100 text-base lg:text-lg">Theo dõi tất cả hoạt động gửi xe của bạn</p>
                        </div>
                        <div className="bg-white bg-opacity-20 p-3 lg:p-4 rounded-full self-start lg:self-auto">
                            <History className="h-6 w-6 lg:h-8 lg:w-8" />
                        </div>
                    </div>
                </div>
                <div className="flex justify-center items-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
                    <span className="ml-3 text-gray-600">Đang tải lịch sử...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-8">
            {/* Header */}
            <div className="bg-gradient-to-r from-cyan-600 via-blue-600 to-cyan-800 rounded-2xl p-4 lg:p-8 text-white shadow-2xl">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
                    <div>
                        <h1 className="text-2xl lg:text-3xl font-bold mb-2">Lịch sử gửi xe</h1>
                        <p className="text-cyan-100 text-base lg:text-lg">Theo dõi tất cả hoạt động gửi xe của bạn</p>
                    </div>
                    <div className="bg-white bg-opacity-20 p-3 lg:p-4 rounded-full self-start lg:self-auto">
                        <History className="h-6 w-6 lg:h-8 lg:w-8" />
                    </div>
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                    {error}
                </div>
            )}

            {/* Quick Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
                {quickStats.map((stat, index) => {
                    const Icon = stat.icon;
                    return (
                        <div key={index} className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-gray-600 mb-1">{stat.title}</p>
                                    <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                                </div>
                                <div className={`p-3 rounded-xl ${stat.color} shadow-lg`}>
                                    <Icon className="h-6 w-6 text-white" />
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* History Table */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <h2 className="text-xl font-semibold text-gray-900">Chi tiết lịch sử gửi xe</h2>
                        <div className="flex items-center gap-2">
                            {isFiltered && (
                                <button
                                    onClick={() => { setFromDate(""); setToDate(""); }}
                                    className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1 px-2 py-2"
                                >
                                    <X className="h-4 w-4" /> Bỏ lọc
                                </button>
                            )}
                            <button
                                onClick={() => setShowDateFilter((v) => !v)}
                                className={`px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors ${showDateFilter || isFiltered ? "bg-cyan-700 text-white" : "bg-cyan-600 text-white hover:bg-cyan-700"}`}
                            >
                                <Calendar className="h-4 w-4" />
                                <span>Lọc theo ngày{isFiltered ? " (đang lọc)" : ""}</span>
                            </button>
                        </div>
                    </div>
                    {showDateFilter && (
                        <div className="mt-4 flex flex-col sm:flex-row sm:items-end gap-3">
                            <label className="text-sm text-gray-700">
                                <span className="block mb-1">Từ ngày</span>
                                <input
                                    type="date"
                                    value={fromDate}
                                    max={toDate || todayInput()}
                                    onChange={(e) => setFromDate(e.target.value)}
                                    className="border border-gray-300 rounded-lg px-3 py-2 bg-white"
                                />
                            </label>
                            <label className="text-sm text-gray-700">
                                <span className="block mb-1">Đến ngày</span>
                                <input
                                    type="date"
                                    value={toDate}
                                    min={fromDate || undefined}
                                    max={todayInput()}
                                    onChange={(e) => setToDate(e.target.value)}
                                    className="border border-gray-300 rounded-lg px-3 py-2 bg-white"
                                />
                            </label>
                            <div className="flex gap-2">
                                <button onClick={() => { const t = todayInput(); setFromDate(t); setToDate(t); }} className="text-sm border border-gray-300 bg-white rounded-lg px-3 py-2 hover:bg-gray-50">Hôm nay</button>
                                <button
                                    onClick={() => { setFromDate(daysAgoInput(6)); setToDate(todayInput()); }}
                                    className="text-sm border border-gray-300 bg-white rounded-lg px-3 py-2 hover:bg-gray-50"
                                >
                                    7 ngày qua
                                </button>
                            </div>
                        </div>
                    )}
                </div>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Phương tiện
                                </th>
                                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Thời gian vào
                                </th>
                                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Thời gian ra
                                </th>
                                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Chi phí
                                </th>
                                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Trạng thái
                                </th>
                                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Ảnh đối soát
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {filteredData.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                                        <History className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                                        {isFiltered ? (
                                            <p className="text-lg font-medium">Không có lượt gửi xe nào trong khoảng ngày đã chọn</p>
                                        ) : (
                                            <>
                                                <p className="text-lg font-medium">Chưa có lịch sử gửi xe</p>
                                                <p className="text-sm">Hãy đăng ký phương tiện và bắt đầu sử dụng dịch vụ</p>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            ) : (
                                filteredData.map((session) => (
                                    <tr key={session.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-6 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <div className="bg-cyan-100 p-3 rounded-full mr-4">
                                                    <Bike className="h-5 w-5 text-cyan-600" />
                                                </div>
                                                <div>
                                                    <div className="text-sm font-medium text-gray-900">{session.vehicle.license_plate}</div>
                                                    <div className="text-sm text-gray-500">{session.vehicle.brand} {session.vehicle.model}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-6 whitespace-nowrap text-sm text-gray-900">
                                            {formatDateTime(session.entry_time)}
                                        </td>
                                        <td className="px-6 py-6 whitespace-nowrap text-sm text-gray-900">
                                            {session.exit_time ? formatDateTime(session.exit_time) : "---"}
                                        </td>
                                        <td className="px-6 py-6 whitespace-nowrap text-sm font-medium text-gray-900">
                                            {session.exit_time ? `${session.fee.toLocaleString('vi-VN')}₫` : "---"}
                                        </td>
                                        <td className="px-6 py-6 whitespace-nowrap">
                                            <div className="flex items-center">
                                                {getStatusIcon(session)}
                                                <span className={`ml-2 inline-flex px-3 py-1 text-xs font-semibold rounded-full ${getStatusColor(session)}`}>
                                                    {getStatusText(session)}
                                                </span>
                                            </div>
                                            {session.exit_source === "OFFLINE_SYNC" && (
                                                <div className="mt-1 text-xs text-amber-700">Ghi nhận khi cổng mất mạng</div>
                                            )}
                                        </td>
                                        <td className="px-6 py-6 whitespace-nowrap">
                                            <div className="flex space-x-2">
                                                {[["Vào", session.entry_image_url], ["Ra", session.exit_image_url]].map(([label, url]) => {
                                                    const src = assetUrl(url);
                                                    return src ? (
                                                        <a key={label} href={src} target="_blank" rel="noreferrer" title={`Ảnh lúc ${label}`}>
                                                            <img src={src} alt={`Ảnh ${label}`} className="h-12 w-20 object-cover rounded border" />
                                                        </a>
                                                    ) : null;
                                                })}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}